import { apiGet, apiGetBlob, apiPost, apiPut, HttpError } from './http';

// 字段统一 camelCase — 由 http.ts 的 pascalToCamel 自动从后端 PascalCase 转换
export interface WxworkPublicConfig {
  corpId: string;
  agentId: string;
  oAuth2RedirectUrl?: string;
}

export interface InternalAuthResult {
  token: string;
  expiresAt: string;
  userId: string;
  username: string;
  wxworkUserId: string;
}

export interface ExternalContact {
  externalUserId: string;
  name: string;
  avatar?: string;
  remark?: string;
  addedAt?: string;
  boundCustomerId?: string;
  boundCustomerName?: string;
}

export interface UnboundOrder {
  orderId: string;
  customerId?: string;
  orderNumber: string;
  primaryContactDisplay: string;
  amount: number;
  createdAt: string;
}

export interface SendFormCardResponse {
  sent: boolean;
  token: string;
  miniProgramPath?: string;
  cardUrl?: string;
  reason?: string;
}

export interface BroadcastResult {
  scannedTasks: number;
  pushed: number;
  skipped: number;
  failed: number;
}

/* ── Auth ─────────────────────────────────────── */

export function getWxworkConfig() {
  return apiGet<WxworkPublicConfig>('/api/internal/wxwork-config');
}

export function exchangeOAuth2Code(code: string, corpId: string) {
  return apiPost<InternalAuthResult>('/api/internal/auth/wxwork-oauth2', { code, corpId });
}

/* ── 客户管理 ──────────────────────────────────── */

export interface UnboundContact {
  externalUserId: string;
  name: string;
  avatar?: string;
  remark?: string;
  addedAt?: string;
  isFriend: boolean;
  inGroupNames: string[];
}

export function listUnboundContacts() {
  return apiGet<UnboundContact[]>('/api/internal/customers/unbound-contacts');
}

export function listUnboundOrders(days = 30) {
  return apiGet<UnboundOrder[]>(`/api/internal/customers/unbound-orders?days=${days}`);
}

export function listOrderCandidates(externalUserId: string) {
  return apiGet<UnboundOrder[]>(
    `/api/internal/external-contacts/${encodeURIComponent(externalUserId)}/order-candidates`);
}

/** 订单专属认领小程序码 — 发给闲鱼买家, 扫码直接认领订单 */
export function fetchOrderClaimQrCode(orderId: string) {
  return apiGetBlob(`/api/internal/orders/${orderId}/claim-qrcode`);
}

/** 闲鱼通用认领小程序码 (公开, 可放进闲管家自动回复/自动发货配图) */
export const XIANYU_CLAIM_CODE_URL = '/api/public/miniprogram-code?key=xianyu';

export function listExternalContactCandidates(orderId: string) {
  return apiGet<ExternalContact[]>(
    `/api/internal/orders/${orderId}/external-contact-candidates`);
}

/**
 * @param externalDisplayName 外部联系人企微昵称(可选). 传了就同时在 customer.contacts 里写入一条
 * WeCom 联系方式, 桌面端"客户详情→联系方式"能看到绑定. 不传只设扁平字段, 桌面端看不到.
 * @param externalAvatarUrl 外部联系人头像 URL(可选). 透传后写到 CustomerContact.AvatarUrl,
 * 桌面端"客户列表"WeCom 徽章会用它替代默认渠道图标.
 */
export function bindCustomerToExternalUser(
  customerId: string,
  externalUserId: string | null,
  externalDisplayName?: string | null,
  externalAvatarUrl?: string | null,
) {
  return apiPost(`/api/internal/customers/${customerId}/bind-external-user`, {
    externalUserId,
    externalDisplayName,
    externalAvatarUrl,
  });
}

export interface CreateFromExternalContactResponse {
  customerId: string;
  displayName: string;
}

export function createCustomerFromExternalContact(
  externalUserId: string,
  displayName?: string,
  avatarUrl?: string | null,
) {
  return apiPost<CreateFromExternalContactResponse>(
    '/api/internal/customers/from-external-contact',
    { externalUserId, displayName, avatarUrl });
}

/* ── 发送填表小程序卡片 ────────────────────────── */

export function sendOrderFormCard(orderId: string, externalUserId: string, chatId?: string) {
  return apiPost<SendFormCardResponse>(
    `/api/internal/orders/${orderId}/send-form-card`,
    { externalUserId, chatId });
}

/* ── 客户群广播 ────────────────────────────────── */

export function broadcastDailyOverview(orderId?: string) {
  return apiPost<BroadcastResult>('/api/internal/broadcasts/daily-overview', { orderId });
}

/* ── 客户群 / chats ───────────────────────────── */

export interface WxworkChatDto {
  chatId: string;
  name: string;
  ownerWxworkUserId: string;
  memberCount: number;
  createdAt?: string;
}

export function listWxworkChats() {
  return apiGet<WxworkChatDto[]>('/api/wxwork/chats');
}

/* ── 客户群成员 ────────────────────────────────── */

export interface ExternalChatMember {
  externalUserId: string;
  name?: string;
  joinTime?: string;
  boundCustomerId?: string;
  boundCustomerName?: string;
}

export interface InternalChatMember {
  wxworkUserId: string;
  name?: string;
  joinTime?: string;
}

export interface ChatMembersDto {
  chatId: string;
  externalMembers: ExternalChatMember[];
  internalMembers: InternalChatMember[];
}

export function listChatMembers(chatId: string) {
  return apiGet<ChatMembersDto>(
    `/api/internal/chats/${encodeURIComponent(chatId)}/members`);
}

/* ── 接客员管理 (Greeters) ─────────────────────── */

export interface Greeter {
  catUserId: string;
  wxworkUserId: string;
  displayName?: string;
  isActiveGreeter: boolean;
  boundAt?: string;
  /** 是否为当前登录员工自己 */
  isMe: boolean;
  /** 企微头像 URL (Tencent CDN, 后端缓存 1h), 可空 */
  avatar?: string;
  /** 个人客服二维码 URL (员工自传, 小程序客户长按加好友), 可空 */
  qrCodeUrl?: string;
  /** 当前账号能不能改这位员工的二维码与在岗: 自己关联的成员, 或有『人员与权限』; 没有这个字段时按可改 */
  canEdit?: boolean;
}

/** 改别人的二维码 / 在岗被拒 (按钮禁用说明与服务器 403 同一句) */
export const GREETER_NOT_OWNED = '只能改自己的；负责人可以改所有人的';

/** 能不能改这位员工的二维码与在岗 */
export const canEditGreeter = (g: Pick<Greeter, 'canEdit'>): boolean => g.canEdit !== false;

export interface ToggleGreeterResult {
  isActiveGreeter: boolean;
}

/**
 * @param myWxworkUserId 当前登录员工的 wxwork_userid (从 auth store 取);
 * 后端用它精确匹配 IsMe — 共享 Cat 账号下多员工时必须传, 否则 isMe 会错认人.
 */
export function listGreeters(myWxworkUserId?: string) {
  const q = myWxworkUserId ? `?me=${encodeURIComponent(myWxworkUserId)}` : '';
  return apiGet<Greeter[]>(`/api/internal/wxwork/greeters${q}`);
}

export function setGreeter(wxworkUserId: string, active: boolean) {
  return apiPost<ToggleGreeterResult>(
    `/api/internal/wxwork/greeters/${encodeURIComponent(wxworkUserId)}/set`,
    { active });
}

/**
 * 给指定 wxwork 员工设置/更新客服二维码 URL.
 * 先上传到 /api/uploads/screenshot 拿 URL, 再调本 API 写入 binding.
 *
 * wxworkUserId 必填 — 共享 Cat 账号场景下 (一个 catUserId 挂多个企微员工,
 * 如 milele 账号下挂 mifai/TangYuZe), 旧版 /me/qrcode 端点按 catUserId 找
 * binding 会命中第一个返回的 (顺序不确定), 表现为'点鱼鱼上传却写到了蛙蛙'
 * 这种错位 bug.  现在按 wxworkUserId 显式定位, 后端再校验
 * binding.UserId == catUserId 防止跨账号操作.
 */
export function setGreeterQrCode(wxworkUserId: string, qrCodeUrl: string | null) {
  return apiPost<{ ok: boolean }>(
    `/api/internal/wxwork/greeters/${encodeURIComponent(wxworkUserId)}/qrcode`,
    { qrCodeUrl });
}

// ── 可用任务收件箱 ──────────────────────────────────────────────

/** 收件箱一条卡片 — 客户提交需求表后建的任务, 含关联订单 + 客户 + 游戏信息 */
export interface PendingTaskCard {
  taskId: string;
  taskNumber: string;
  title: string;
  /** Available / Busy / Waiting / Maintenance / Completed */
  status: string;
  customerName?: string | null;
  customerContactInfo?: string | null;
  customerContactType?: string | null;
  orderId?: string | null;
  orderNumber?: string | null;
  /** Manual / Xianguanjia / XianYu / Taobao */
  orderSource?: string | null;
  /** 外部平台订单的商品标题 (闲鱼/淘宝同步过来时填) */
  orderProductTitle?: string | null;
  externalOrderNo?: string | null;
  gamePlatform?: string | null;
  serverRegion?: string | null;
  characterName?: string | null;
  notes?: string | null;
  createdAt: string;
}

/** 收件箱分页 + 三档计数 (可用 = 任务在做、号现在没人登 / 使用中 = 有人正登着 / 已完成) */
export interface PendingTasksPage {
  items: PendingTaskCard[];
  totalAvailable: number;
  totalBusy: number;
  totalCompleted: number;
}

/** 取当前账号名下"可用"状态的任务 — DraftInboxPage 主调用 */
export function listPendingTasks(skip = 0, take = 50) {
  return apiGet<PendingTasksPage>(
    `/api/internal/tasks/pending?skip=${skip}&take=${take}`);
}

// ── 手机任务页 (任务列表 /tasks 与详情 /tasks/:id) ─────────────────

/** 任务状态枚举名: 可用 (任务在做、号现在没人登) / 使用中 (有人正登着) / 等待中 / 维护中 / 已完成 */
export type TaskStatus = 'Available' | 'Busy' | 'Waiting' | 'Maintenance' | 'Completed';

/** 列表筛选: 单个状态, 或 InProgress (进行中 = 可用 + 使用中); 空串表示不限 */
export type TaskListFilter = TaskStatus | 'InProgress' | '';

/** 任务列表的一行 */
export interface MobileTaskListItem {
  taskId: string;
  taskNumber: string;
  title: string;
  status: TaskStatus;
  /** 订单尾号; 没有订单时缺省 */
  orderTail?: string;
  /** 买家显示名 (闲鱼昵称), 没有订单时为任务上的客户名 */
  customerName?: string;
  characterName?: string;
  /** 上号资料是否已齐 */
  loginInfoReady: boolean;
  /** 内容是否已确认 */
  contentReady: boolean;
  /** 客户是否已加企业微信 */
  wxworkReady: boolean;
  /** 还没人打开过 */
  isNew: boolean;
  /** 本次进入使用中的时间 */
  startedAt?: string;
  lastModifiedByName?: string;
  /** Desktop / Mobile */
  lastModifiedSource?: string;
  lastModifiedAt?: string;
  createdAt: string;
}

/** 各筛选的数量 (不含已退款订单的任务, 不受搜索影响) */
export interface MobileTaskStatusCounts {
  inProgress: number;
  available: number;
  busy: number;
  waiting: number;
  maintenance: number;
  completed: number;
}

/** 任务列表一页 */
export interface MobileTaskListPage {
  items: MobileTaskListItem[];
  /** 符合条件的总数 */
  total: number;
  counts: MobileTaskStatusCounts;
}

/** 任务详情里的子任务 */
export interface MobileTaskItem {
  id: string;
  title: string;
  isCompleted: boolean;
  displayOrder: number;
}

/** 任务详情里的订单摘要 */
export interface MobileTaskOrder {
  orderId: string;
  orderNumber: string;
  externalOrderNo?: string;
  /** 订单尾号 (平台订单号后 4 位, 没有时取 Cat 订单号后 4 位) */
  orderTail: string;
  /** 买家显示名 (闲鱼订单为闲鱼昵称) */
  customerName?: string;
  productTitle?: string;
  /** 订单金额 (元) */
  amount: number;
  status: string;
}

/** 任务详情里的上号信息 (不含密码) */
export interface MobileTaskLoginInfo {
  gamePlatform: string;
  serverRegion: string;
  serverZone: string;
  characterName?: string;
  /** QRCode / SMS / AccountPassword */
  verificationMethod: string;
  verificationMethodLabel: string;
  gameAccount?: string;
  /** 是否填了密码 */
  hasPassword: boolean;
  loginDevice?: string;
  /** WeGame 登录方式 (WeChat / QQ); 不是 WeGame 或未知时缺省 */
  weGameLoginMethod?: string | null;
  /** WeGame 登录方式 (微信 / QQ) */
  weGameLoginMethodLabel?: string | null;
}

/** 聊天 AI 草稿里的一项 */
export interface MobileTaskDraftItem {
  text: string;
  /** 聊天原话; 没找到时缺省 */
  quote?: string;
}

/** 聊天 AI 草稿 */
export interface MobileTaskDraft {
  title?: string;
  items: MobileTaskDraftItem[];
  requirements?: string;
  /** 谈定价格 (元) */
  agreedPrice?: number;
  /** 校验提示 */
  warnings: string[];
}

/** 内容确认记录 */
export interface MobileTaskConfirmation {
  confirmedAt: string;
  /** 定稿是否改过草稿 */
  draftEdited?: boolean;
}

/** 手机任务详情 (不含密码; 密码走 getTaskCredentials) */
export interface MobileTaskDetail {
  taskId: string;
  taskNumber: string;
  /** 版本号: 保存时原样带回, 期间被别处改过时服务器返回 409 */
  version: number;
  title: string;
  status: TaskStatus;
  progress: number;
  /** 本次进入使用中的时间 */
  startedAt?: string;
  /** 第一次进入使用中的时间 */
  firstBusyAt?: string;
  completedAt?: string;
  /** Manual / Product / Customer / ChatAiDraft / None */
  contentSource: string;
  /** Confirmed / Pending */
  contentStatus: string;
  contentPending: boolean;
  loginInfoReady: boolean;
  wxworkReady: boolean;
  isNew: boolean;
  isRefunded: boolean;
  /** 最后修改人 (手机修改时为员工名); 电脑修改或未知时缺省 */
  lastModifiedByName?: string;
  /** Desktop / Mobile */
  lastModifiedSource?: string;
  lastModifiedAt?: string;
  notes?: string;
  items: MobileTaskItem[];
  order?: MobileTaskOrder;
  loginInfo: MobileTaskLoginInfo;
  draft?: MobileTaskDraft;
  confirmation?: MobileTaskConfirmation;
  /** 暂停的原因、说明与恢复时间; 不在暂停或没记原因时缺省 */
  pause?: MobileTaskPause;
}

/** 暂停原因: 客户自用 / 待客户配合 / 游戏维护 / 内部原因 */
export type PauseReason = 'customerUsing' | 'awaitingCustomer' | 'gameMaintenance' | 'internal';

/** 任务详情里的暂停信息 */
export interface MobileTaskPause {
  reason: PauseReason;
  note?: string;
  /** 约定的恢复时间; 没约定时缺省 */
  resumeAt?: string;
  /** 暂停开始的时间 */
  pausedAt?: string;
}

/** 暂停时带的参数: 游戏维护对应维护中, 其余原因对应等待中; 内部原因必填说明 */
export interface MobileTaskPauseInput {
  reason: PauseReason;
  note?: string;
  /** 约定的恢复时间 (ISO); 不约定时不传 */
  resumeAt?: string;
}

/** 账号和密码 (明文) */
export interface MobileTaskCredentials {
  gameAccount?: string;
  gamePassword?: string;
}

/**
 * 写操作结果: conflict 为 true 表示期间任务被别处改过, 本次没有保存, detail 是服务器上的最新数据
 */
export interface MobileTaskWriteResult {
  detail: MobileTaskDetail;
  conflict: boolean;
}

/** 修改内容时的一项子任务: 带 id 为修改已有项, 不带为新增; 未出现的已有项被删除 */
export interface MobileTaskContentItemInput {
  id?: string;
  title: string;
}

function taskPath(taskId: string) {
  return `/api/internal/tasks/${encodeURIComponent(taskId)}`;
}

/** 执行写操作, 把 409 (版本已变, 响应体是最新详情) 转成 conflict 结果 */
async function taskWrite(request: Promise<MobileTaskDetail>): Promise<MobileTaskWriteResult> {
  try {
    return { detail: await request, conflict: false };
  } catch (e) {
    if (e instanceof HttpError && e.status === 409) {
      const latest = e.json<MobileTaskDetail>();
      if (latest?.taskId) return { detail: latest, conflict: true };
    }
    throw e;
  }
}

/** 任务列表: 按状态筛选、按订单尾号 / 闲鱼昵称 / 角色名 / 任务号搜索; 不含已退款订单的任务 */
export function listMobileTasks(status: TaskListFilter, q: string, skip = 0, take = 30) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (q.trim()) params.set('q', q.trim());
  params.set('skip', String(skip));
  params.set('take', String(take));
  return apiGet<MobileTaskListPage>(`/api/internal/tasks?${params.toString()}`);
}

/** 任务详情 (不含密码); 任务带"新"标记时服务器顺带去掉 */
export function getMobileTask(taskId: string) {
  return apiGet<MobileTaskDetail>(taskPath(taskId));
}

/** 点"显示密码"时取账号和密码 */
export function getTaskCredentials(taskId: string) {
  return apiPost<MobileTaskCredentials>(`${taskPath(taskId)}/credentials`);
}

/**
 * 切任务状态 (任意状态之间); 切到已完成时服务器通知已订阅的客户。
 * 暂停 (等待中 / 维护中) 时带 pause; 已暂停的任务再次提交可改原因、说明或恢复时间
 */
export function changeTaskStatus(taskId: string, status: TaskStatus, expectedVersion: number, pause?: MobileTaskPauseInput) {
  return taskWrite(apiPost<MobileTaskDetail>(`${taskPath(taskId)}/status`, { status, expectedVersion, ...(pause ? { pause } : {}) }));
}

/** 整体修改本单内容 (标题与子任务); 内容待确认时保存即确认 */
export function updateTaskContent(
  taskId: string,
  title: string,
  items: MobileTaskContentItemInput[],
  expectedVersion: number,
) {
  return taskWrite(apiPut<MobileTaskDetail>(`${taskPath(taskId)}/content`, { title, items, expectedVersion }));
}

/** 原样确认待确认的内容 (AI 草稿等) */
export function confirmTaskContent(taskId: string, expectedVersion: number) {
  return taskWrite(apiPost<MobileTaskDetail>(`${taskPath(taskId)}/confirm-content`, { expectedVersion }));
}

/** 勾选 / 取消子任务完成 */
export function toggleTaskItem(taskId: string, itemId: string, expectedVersion: number) {
  return taskWrite(apiPost<MobileTaskDetail>(
    `${taskPath(taskId)}/items/${encodeURIComponent(itemId)}/toggle`, { expectedVersion }));
}

/** 修改备注; 空白表示清空 */
export function updateTaskNotes(taskId: string, notes: string, expectedVersion: number) {
  return taskWrite(apiPut<MobileTaskDetail>(`${taskPath(taskId)}/notes`, { notes, expectedVersion }));
}
